import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAPIClientRequest, useLanguageSwitcher, useSkinSwitcher } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import AdminSkinSwitcher from 'c-wrapper/Components/SkinLangSwitcher/AdminSkinSwitcher';
import { Alert, Button, Checkbox, DialogV2, LoadingSpinner, SelectField } from 'c-components';
import { DEFAULT_SKIN_ID } from 'c-wrapper/Slices/UI/ui-selectors';
import { useCommonTranslation } from 'c-translation';
import { Stack, Typography } from '@mui/material';
import { NetworkRequestState } from 'c-data-layer';
import { useUserPermissions } from 'c-auth-module/Hooks';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { LANG_en_gb, track } from 'c-lib';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { useBoolean } from 'react-hanger';
import { useEntityData } from 'c-data';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import {
    appendPcaFilterParams,
    canFilterPca,
    initialPcaDateRange,
    initialPcaFilters,
} from './pcaFilters';

type BooleanState = {
    value: boolean;
    toggle: () => void;
    setTrue: () => void;
    setFalse: () => void;
};
interface PcaDialogProps {
    id: string | number;
    pcaState: BooleanState;
    isLinear: boolean;
    campaignStartDate: string | null;
    campaignEndDate: string | null;
}

function formatDateToDDMMYYYY(date: Date | null): string {
    if (!date) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}
const pdfPerms = [PermissionName.PdfPcaReportRead];
const pptPerms = [PermissionName.PowerpointPcareportRead];

const sanitizeForFilename = (s: string) =>
    (s ?? '')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

const makePdfFilename = (campaignName?: string, campaignId?: string | number) => {
    const safeName = sanitizeForFilename(campaignName || 'Campaign');
    const safeId = String(campaignId ?? '').trim();
    const core = safeId
        ? `${safeName}_UniLED ${safeId}_UniLED Report`
        : `${safeName}_UniLED Report`;
    return `${core}.pdf`;
};

const PptPCADialog: React.FC<PcaDialogProps> = ({
    id,
    pcaState,
    isLinear,
    campaignStartDate,
    campaignEndDate,
}) => {
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id: Number(id) });
    const t = useCommonTranslation();
    const { selectedSkin } = useSkinSwitcher();
    const [skin, setSkin] = useState<number | null>(selectedSkin?.id);
    const { hasAll } = useUserPermissions();
    const adminPerms = useMemo(() => hasAll([PermissionName.Admin]), [hasAll]);

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        campaignStartDate ? new Date(campaignStartDate) : null,
        campaignEndDate ? new Date(campaignEndDate) : null,
    ]);
    const third_color = useBoolean(false);
    const [useDateRange, setUseDateRange] = useState(0);
    const { lang } = useLanguageSwitcher();
    const [language, setLanguage] = useState<string>(lang === LANG_en_gb ? 'GB' : 'US');
    const [reportType, setReportType] = useState<string>('');

    // UL-8220: the reporting page's filters, applied (behind a checkbox) to a linear campaign's
    // digital PCA only; a DSP campaign or one with classic units sends none. The page's date
    // range is copied in each time the dialog opens.
    const { advancedFilters, urlDateValue } = useCampaignWrapperState(Number(id));
    // live data, whatever environment the page shows: the PCA always reports live, and the API's
    // Campaign::canFilterPca() checks the same
    const filtersAllowed = canFilterPca(isLinear, campaign?.pca_display_types?.live);
    const pageFilters = useMemo(() => initialPcaFilters(advancedFilters), [advancedFilters]);
    const hasPageFilters = filtersAllowed && Object.keys(pageFilters).length > 0;
    const applyFilters = useBoolean(true);
    const filters = useMemo(
        () => (hasPageFilters && applyFilters.value ? pageFilters : {}),
        [hasPageFilters, applyFilters.value, pageFilters],
    );
    const pageFiltersLabel = useMemo(
        () =>
            Object.entries(pageFilters)
                .map(
                    ([key, values]) =>
                        `${t(`Modules.Main.Campaigns.Overview.Reports.fields.${key}`)}: ${(
                            values ?? []
                        ).join(', ')}`,
                )
                .join(' · '),
        [pageFilters, t],
    );
    const pageDateRange = useMemo(() => initialPcaDateRange(urlDateValue), [urlDateValue]);

    const isOpen = pcaState.value;
    useEffect(() => {
        if (!isOpen) return;
        applyFilters.setTrue();
        if (pageDateRange) {
            setUseDateRange(1);
            setDateRange(pageDateRange);
        }
        // only when the dialog opens: later page changes must not overwrite what was picked here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const { start, isLoading, error, requestState } = useAPIClientRequest(
        ApiClient.Entities.Campaign.campaignPowerpoint,
    );
    const [pdfFetching, setPdfFetching] = useState(false);
    const [pdfReadyUrl, setPdfReadyUrl] = useState<string | null>(null);
    const [pdfFilename, setPdfFilename] = useState<string>('report.pdf');
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [showPdfConfirm, setShowPdfConfirm] = useState(false);

    const isDateRangeComplete = useMemo(() => {
        const [startDate, endDate] = dateRange;
        return !!startDate && !!endDate;
    }, [dateRange]);

    useEffect(() => {
        setReportType(isLinear ? 'Linear_report' : 'DSP_report');
    }, [isLinear]);
    const onSkinSelected = useCallback((val: number) => setSkin(val), []);

    const onSubmit = useCallback(() => {
        const [startDate, endDate] = dateRange;
        track('generate_pca', {
            campaign_id: id,
            report_type: reportType,
            date_range: useDateRange === 1,
            filters: Object.keys(filters),
        });
        if (useDateRange === 1) {
            start(
                id,
                third_color.value,
                reportType,
                skin || 0,
                language,
                !isDateRangeComplete ? null : formatDateToDDMMYYYY(startDate),
                !isDateRangeComplete ? null : formatDateToDDMMYYYY(endDate),
                filters,
            );
        } else {
            start(id, third_color.value, reportType, skin || 0, language, null, null, filters);
        }
    }, [
        filters,
        dateRange,
        useDateRange,
        start,
        id,
        third_color.value,
        reportType,
        skin,
        language,
        isDateRangeComplete,
    ]);

    const buildPdfUrl = useCallback(() => {
        const [startDate, endDate] = dateRange;
        const params = new URLSearchParams({
            third_color: String(third_color.value),
            powerpoint_template: reportType,
            skin_id: String(skin || 0),
            language,
        });
        if (useDateRange === 1 && startDate && endDate) {
            params.set('start_date', formatDateToDDMMYYYY(startDate));
            params.set('end_date', formatDateToDDMMYYYY(endDate));
        }
        appendPcaFilterParams(params, filters);
        return `/api/v1/portal/campaign/${id}/pdf?${params.toString()}`;
    }, [dateRange, filters, id, language, reportType, skin, third_color.value, useDateRange]);

    const onPreparePdf = useCallback(async () => {
        try {
            setPdfError(null);
            setPdfReadyUrl(null);
            setPdfFetching(true);

            const url = buildPdfUrl();
            const res = await fetch(url, { method: 'GET', credentials: 'include' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const desiredName = makePdfFilename(campaign.name, id);

            // Still parse server filename as a fallback (or for logging, if you like)
            const cd = res.headers.get('Content-Disposition') || '';
            const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
            const serverName = decodeURIComponent(match?.[1] || match?.[2] || '');

            const finalName =
                desiredName ||
                (serverName.endsWith('.pdf') ? serverName : `${serverName}.pdf`) ||
                'report.pdf';
            setPdfFilename(finalName);

            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            setPdfReadyUrl(objectUrl);
            setShowPdfConfirm(true);
        } catch {
            setPdfError(t('Modules.Main.Campaigns.Overview.Pca.pdfFailed'));
        } finally {
            setPdfFetching(false);
        }
    }, [buildPdfUrl, campaign.name, id, t]);

    const confirmDownload = useCallback(() => {
        if (!pdfReadyUrl) return;
        const a = document.createElement('a');
        a.href = pdfReadyUrl;
        a.download = pdfFilename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(pdfReadyUrl);
        setPdfReadyUrl(null);
        setShowPdfConfirm(false);
    }, [pdfReadyUrl, pdfFilename]);

    const cancelDownload = useCallback(() => {
        if (pdfReadyUrl) URL.revokeObjectURL(pdfReadyUrl);
        setPdfReadyUrl(null);
        setShowPdfConfirm(false);
    }, [pdfReadyUrl]);

    const reportTypeOptions = useMemo(
        () =>
            isLinear
                ? [
                      {
                          label: t('Modules.Main.Campaigns.Overview.Pca.linearReport'),
                          value: 'Linear_report',
                      },
                  ]
                : [
                      {
                          label: t('Modules.Main.Campaigns.Overview.Pca.dspReport'),
                          value: 'DSP_report',
                      },
                  ],
        [isLinear, t],
    );

    const disableActions = isLoading || pdfFetching || (useDateRange === 1 && !isDateRangeComplete);

    return (
        <>
            <DialogV2
                open={showPdfConfirm}
                onClose={cancelDownload}
                title="Modules.Main.Campaigns.Overview.Pca.pdfReadyTitle"
            >
                <Stack gap={2}>
                    <Alert severity="info">
                        {t('Modules.Main.Campaigns.Overview.Pca.pdfReadyMessage')}
                    </Alert>
                    <Stack direction="row" gap={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={cancelDownload}>
                            {t('Modules.Main.Campaigns.Overview.Pca.no')}
                        </Button>
                        <Button onClick={confirmDownload}>
                            {t('Modules.Main.Campaigns.Overview.Pca.yes')}
                        </Button>
                    </Stack>
                </Stack>
            </DialogV2>

            <DialogV2
                open={pcaState.value}
                onClose={pcaState.setFalse}
                title="Modules.Main.Campaigns.Overview.Pca.title"
            >
                {adminPerms && (
                    <Stack>
                        <AdminSkinSwitcher
                            disabled={disableActions}
                            onChange={onSkinSelected}
                            additionalOptions={[]}
                            selectedSkinId={skin ?? DEFAULT_SKIN_ID}
                        />
                    </Stack>
                )}

                <IfHasAllPermissions permissions={[PermissionName.AdminPca_exportReport_type]}>
                    <SelectField
                        options={reportTypeOptions}
                        value={reportType}
                        onChange={e => setReportType(e.target.value as string)}
                    />
                </IfHasAllPermissions>
                <IfHasAllPermissions permissions={[PermissionName.Admin]}>
                    <SelectField
                        value={language}
                        onChange={e => setLanguage(e.target.value as string)}
                        options={[
                            { label: 'UK', value: 'GB' },
                            { label: 'US', value: 'US' },
                        ]}
                    />
                </IfHasAllPermissions>
                <SelectField
                    value={useDateRange}
                    onChange={e => setUseDateRange(e.target.value as number)}
                    options={[
                        { label: 'Campaign Target', value: 0 },
                        { label: 'Date Range', value: 1 },
                    ]}
                />
                <IfHasAllPermissions permissions={[PermissionName.Admin]}>
                    <Checkbox
                        checked={third_color.value}
                        onClick={third_color.toggle}
                        label={t('Modules.Main.Campaigns.Overview.Pca.thirdColor')}
                    />
                </IfHasAllPermissions>

                {useDateRange === 1 && (
                    <DateRangePicker
                        value={dateRange}
                        onChange={newValue => setDateRange(newValue)}
                        localeText={{ start: 'Start date', end: 'End date' }}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                )}

                {hasPageFilters && (
                    <Stack>
                        <Checkbox
                            checked={applyFilters.value}
                            onClick={applyFilters.toggle}
                            disabled={disableActions}
                            label={t(
                                'Modules.Main.Campaigns.Overview.Pca.Filters.applyPageFilters',
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            data-testid="pca-page-filters"
                        >
                            {pageFiltersLabel}
                        </Typography>
                    </Stack>
                )}

                <IfHasAllPermissions permissions={pptPerms}>
                    <Button onClick={onSubmit} disabled={disableActions}>
                        {t('Modules.Main.Campaigns.Overview.Pca.submit')}
                    </Button>
                </IfHasAllPermissions>

                <IfHasAllPermissions permissions={pdfPerms}>
                    <Button onClick={onPreparePdf} disabled={disableActions}>
                        {pdfFetching
                            ? t('Modules.Main.Campaigns.Overview.Pca.pdfGenerating')
                            : t('Modules.Main.Campaigns.Overview.Pca.submitPdf')}
                    </Button>
                </IfHasAllPermissions>

                {(isLoading || pdfFetching) && <LoadingSpinner />}

                {error && <Alert severity="error">{String(error)}</Alert>}
                {pdfError && <Alert severity="error">{pdfError}</Alert>}

                {requestState === NetworkRequestState.Success && (
                    <Alert severity="success">
                        {t('Modules.Main.Campaigns.Overview.Pca.success')}
                    </Alert>
                )}
            </DialogV2>
        </>
    );
};

export default PptPCADialog;
